import { useRef, useState, useEffect } from 'react'
import moment from 'moment'
import Cookies from 'universal-cookie'
import s from './RangeDatePicker.scss'
import { COOKIES } from 'utils/constants'
import { utcDate, timezoneDate } from 'utils/helpers'
import { getDateFormat } from 'utils/authService'
import { DayPicker } from 'react-day-picker'
import { usePopper } from 'react-popper'
import { OutsideClickContainer } from 'components/ideal-core-ui'
import cn from 'classnames'

const cookies = new Cookies()

const RangeDatePicker = ({
  startDate,
  endDate,
  onChange,
  disabled,
  className,
  clearable = false
}) => {
  const [selectedRange, setSelectedRange] = useState({
    from: moment().subtract(2, 'weeks').startOf('day').toDate(),
    to: moment().add(6, 'weeks').endOf('day').toDate()
  })
  const [fromValue, setFromValue] = useState('')
  const [toValue, setToValue] = useState('')
  const [isPopperEndOpen, setIsPopperEndOpen] = useState(false)
  const [isPopperStartOpen, setIsPopperStartOpen] = useState(false)
  const [popperStartElement, setPopperStartElement] = useState(null)
  const [popperEndElement, setPopperEndElement] = useState(null)

  const popperStartRef = useRef(null)
  const popperEndRef = useRef(null)

  const popperStart = usePopper(popperStartRef.current, popperStartElement, {
    placement: 'bottom-start'
  })

  const popperEnd = usePopper(popperEndRef.current, popperEndElement, {
    placement: 'bottom-start'
  })

  const setDates = ({ from, to } = {}) => {
    cookies.set(
      COOKIES.DATE_RANGE_FILTERS,
      {
        startDate: utcDate(
          timezoneDate(moment(from).startOf('day').toDate(), null, null, true).toDate()
        ),
        endDate: utcDate(timezoneDate(moment(to).endOf('day').toDate(), null, null, true).toDate())
      },
      {
        path: '/',
        expires: moment().add(8, 'weeks').toDate()
      }
    )

    onChange &&
      onChange({
        from: timezoneDate(moment(from).startOf('day').toDate(), null, null, true).toDate(),
        to: timezoneDate(moment(to).endOf('day').toDate(), null, null, true).toDate()
      })
  }

  useEffect(() => {
    const start = startDate ? timezoneDate(startDate, null, null, true).toDate() : undefined
    const end = endDate ? timezoneDate(endDate, null, null, true).toDate() : undefined

    if (
      moment(start).format().split('T')[0] !== moment(selectedRange?.from).format().split('T')[0] ||
      moment(end).format().split('T')[0] !== moment(selectedRange?.to).format().split('T')[0]
    ) {
      setSelectedRange({
        from: startDate ? moment(timezoneDate(startDate), getDateFormat()).toDate() : undefined,
        to: endDate ? moment(timezoneDate(endDate), getDateFormat()).toDate() : undefined
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDate, endDate])

  useEffect(() => {
    setFromValue(selectedRange?.from ? moment(selectedRange?.from).format(getDateFormat()) : '')
    setToValue(selectedRange?.to ? moment(selectedRange?.to).format(getDateFormat()) : '')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedRange])

  const closePopper = () => {
    setIsPopperStartOpen(false)
    setIsPopperEndOpen(false)
  }

  const handleClick = time => {
    if (!disabled) {
      if (time === 'from') {
        setIsPopperStartOpen(true)
      } else {
        setIsPopperEndOpen(true)
      }
    }
  }

  const handleRangeSelect = (range, day, time) => {
    setSelectedRange({ ...selectedRange, [time]: day })
    if (time === 'from') {
      if (range?.from) {
        setFromValue(moment(range.from).format(getDateFormat()))
      } else {
        setFromValue('')
      }
    }
    if (time === 'to') {
      if (range?.to) {
        setToValue(moment(range.to).format(getDateFormat()))
      } else {
        setToValue('')
      }
    }
    if (range?.from && range?.to) {
      setDates({
        from: time === 'from' ? day : selectedRange.from,
        to: time === 'to' ? day : selectedRange.to
      })
    }
  }

  return (
    <div className={cn(s.dayPickerWrapper, className)}>
      <div ref={popperStartRef}>
        <input
          type="text"
          placeholder="From Date"
          value={fromValue}
          onClick={() => handleClick('from')}
          onChange={() => {}}
          disabled={disabled}
        />
      </div>
      {' – '}
      <div ref={popperEndRef}>
        <input
          type="text"
          placeholder="To Date"
          value={toValue}
          onClick={() => handleClick('to')}
          onChange={() => {}}
          disabled={disabled}
        />
      </div>
      {clearable && (toValue || fromValue) && (
        <span
          className={s.clearButton}
          onClick={() => {
            const cleared = { from: undefined, to: undefined }
            setSelectedRange(cleared)
            setToValue('')
            setFromValue('')
            onChange && onChange(cleared)
          }}
        >
          &times;
        </span>
      )}
      {isPopperStartOpen && (
        <OutsideClickContainer styles={{ height: 'initial' }} closeComponent={closePopper}>
          <div
            tabIndex={-1}
            style={popperStart.styles.popper}
            {...popperStart.attributes.popper}
            ref={setPopperStartElement}
            className={s.popper}
          >
            <DayPicker
              captionLayout="dropdown-buttons"
              fromYear={2000}
              toYear={2100}
              initialFocus={isPopperStartOpen}
              mode="range"
              selected={selectedRange}
              onSelect={(range, day) => handleRangeSelect(range, day, 'from')}
              className={className}
              numberOfMonths={2}
              defaultMonth={selectedRange?.from}
              disabled={{
                after: selectedRange?.to
              }}
            />
          </div>
        </OutsideClickContainer>
      )}
      {isPopperEndOpen && (
        <OutsideClickContainer styles={{ height: 'initial' }} closeComponent={closePopper}>
          <div
            tabIndex={-1}
            style={popperEnd.styles.popper}
            {...popperEnd.attributes.popper}
            ref={setPopperEndElement}
            className={s.popper}
          >
            <DayPicker
              captionLayout="dropdown-buttons"
              fromYear={2000}
              toYear={2100}
              initialFocus={isPopperEndOpen}
              mode="range"
              selected={selectedRange}
              onSelect={(range, day) => handleRangeSelect(range, day, 'to')}
              className={className}
              numberOfMonths={2}
              defaultMonth={selectedRange?.to}
              disabled={{
                before: selectedRange?.from
              }}
            />
          </div>
        </OutsideClickContainer>
      )}
    </div>
  )
}

export default RangeDatePicker
